import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { MAP_POIS } from "@/data/mapPois";
import { RankBadge } from "@/components/RankBadge";
import { Trophy, Crown, Medal, Skull, Loader2, Swords, Castle, Clock } from "lucide-react";

const TOTAL = MAP_POIS.filter((m) => m.c === "alpha").length;

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "à l'instant";
  const m = Math.floor(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
}

const RANK_STYLES = [
  { icon: Crown, color: "#FACC15", ring: "#FACC15" },
  { icon: Medal, color: "#CBD5E1", ring: "#94A3B8" },
  { icon: Medal, color: "#D97706", ring: "#B45309" },
];

export default function Leaderboard() {
  const { data: board = [], isLoading } = useQuery({
    queryKey: ["leaderboard"],
    queryFn: async () => (await api.get("/leaderboard")).data,
    refetchInterval: 30000,
  });

  const { data: events = [] } = useQuery({
    queryKey: ["events"],
    queryFn: async () => (await api.get("/events?limit=25")).data,
    refetchInterval: 30000,
  });

  const ranked = board.filter((b) => b.defeated > 0);
  const podium = ranked.slice(0, 3);

  return (
    <div className="fade-up max-w-4xl space-y-6" data-testid="leaderboard-page">
      <div>
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-primary/80">Communauté</div>
        <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
          <Trophy className="w-7 h-7 text-[#FACC15]" /> Classement des boss
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Tous les dresseurs du serveur, classés par nombre de boss vaincus (sur {TOTAL}).
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-primary"><Loader2 className="w-7 h-7 animate-spin" /></div>
      ) : ranked.length === 0 ? (
        <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-10 text-center text-sm text-muted-foreground" data-testid="leaderboard-empty">
          <Skull className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Personne n'a encore vaincu de boss. Sois le premier à grimper au classement !
        </div>
      ) : (
        <>
          {/* Podium */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            {[1, 0, 2].map((slot) => {
              const p = podium[slot];
              if (!p) return <div key={slot} />;
              const st = RANK_STYLES[slot];
              const RankIcon = st.icon;
              const heights = ["h-28", "h-36", "h-24"];
              const h = slot === 0 ? heights[1] : slot === 1 ? heights[0] : heights[2];
              return (
                <div key={p.user_id} className={`flex flex-col items-center justify-end ${slot === 0 ? "order-2" : slot === 1 ? "order-1" : "order-3"}`}>
                  <div className="relative mb-2">
                    {p.picture ? (
                      <img src={p.picture} alt="" className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover" style={{ border: `2px solid ${st.ring}` }} />
                    ) : (
                      <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-lg font-bold" style={{ background: `${st.color}22`, border: `2px solid ${st.ring}`, color: st.color }}>
                        {p.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <RankIcon className="absolute -top-2 -right-2 w-5 h-5" style={{ color: st.color }} />
                  </div>
                  <div className="text-sm font-semibold truncate max-w-full px-1">{p.name}{p.is_me && <span className="text-primary"> (toi)</span>}</div>
                  <div className="mt-0.5"><RankBadge defeated={p.defeated} size="xs" /></div>
                  <div className={`mt-2 w-full rounded-t-lg ${h} flex items-start justify-center pt-2`} style={{ background: `linear-gradient(180deg, ${st.color}33, transparent)`, borderTop: `2px solid ${st.ring}` }}>
                    <span className="font-head font-bold text-lg" style={{ color: st.color }}>{p.defeated}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Full ranking */}
          <div className="rounded-lg border border-border bg-[hsl(var(--surface))] overflow-hidden" data-testid="leaderboard-list">
            {ranked.map((p, i) => {
              const pct = TOTAL ? Math.round((p.defeated / TOTAL) * 100) : 0;
              return (
                <div
                  key={p.user_id}
                  data-testid={`leaderboard-row-${i}`}
                  className={`flex items-center gap-3 px-4 py-3 border-b border-border last:border-0 ${p.is_me ? "bg-primary/10" : "hover:bg-[hsl(var(--surface-hover))]"} transition-colors`}
                >
                  <span className="w-6 text-center font-mono text-sm font-bold text-muted-foreground shrink-0">{i + 1}</span>
                  {p.picture ? (
                    <img src={p.picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center text-sm font-bold text-primary shrink-0">
                      {p.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate flex items-center gap-2">
                      <span className="truncate">{p.name}{p.is_me && <span className="text-primary"> (toi)</span>}</span>
                      <RankBadge defeated={p.defeated} size="xs" className="shrink-0" />
                    </div>
                    <div className="mt-1 h-1.5 w-full rounded-full bg-black/40 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "#22C55E" }} />
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-head font-bold text-sm" style={{ color: "#22C55E" }}>{p.defeated}</div>
                    <div className="text-[10px] text-muted-foreground">/{TOTAL}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Derniers exploits — feed serveur */}
      <div data-testid="exploits-feed">
        <h2 className="font-head text-lg font-bold flex items-center gap-2 mb-3">
          <Swords className="w-5 h-5 text-primary" /> Derniers exploits
        </h2>
        {events.length === 0 ? (
          <div className="rounded-lg border border-border bg-[hsl(var(--surface))] p-6 text-center text-sm text-muted-foreground">
            Aucune victoire enregistrée pour l'instant. Coche un boss sur la carte pour apparaître ici !
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-[hsl(var(--surface))] overflow-hidden">
            {events.map((e, i) => {
              const tower = e.boss_type === "tower";
              return (
                <div key={i} data-testid={`exploit-row-${i}`} className="flex items-center gap-3 px-4 py-2.5 border-b border-border last:border-0">
                  {e.picture ? (
                    <img src={e.picture} alt="" className="w-7 h-7 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                      {(e.player || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="shrink-0 w-6 flex justify-center" style={{ color: tower ? "#D946EF" : "#EF4444" }}>
                    {tower ? <Castle className="w-4 h-4" /> : <Skull className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0 flex-1 text-sm">
                    <span className="font-semibold">{e.player}</span>
                    <span className="text-muted-foreground"> a vaincu </span>
                    <span className="font-semibold" style={{ color: tower ? "#D946EF" : "#EF4444" }}>{e.boss_name}</span>
                    {e.level ? <span className="text-muted-foreground"> (Nv{e.level})</span> : null}
                  </div>
                  <div className="text-[10px] text-muted-foreground flex items-center gap-1 shrink-0">
                    <Clock className="w-3 h-3" /> {timeAgo(e.timestamp)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
